import React, { useEffect, useState } from 'react';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { Box, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';

function computePercentage(objectRecord: any): number {
  if (objectRecord?.is_permanent) return 100;
  const created = DateTime.fromISO(objectRecord?.created_at).toSeconds();
  const expire = DateTime.fromISO(objectRecord?.expire_at).toSeconds();
  const total = expire - created;
  if (total <= 0) return 0;
  const left = expire - DateTime.now().toSeconds();
  return Math.max(0, Math.min(100, Math.round((left / total) * 100)));
}

// green (#16a81b) at 100%, red (#b51e1b) at 0%
function pickColor(pct: number): string {
  const t = pct / 100;
  const r = Math.round(181 * (1 - t) + 22 * t);
  const g = Math.round(30  * (1 - t) + 168 * t);
  const b = Math.round(27);
  return `rgb(${r},${g},${b})`;
}

function HealthBar({ objectRecord, children }: { objectRecord: any; children: React.ReactNode }) {
  const [pct, setPct] = useState(() => computePercentage(objectRecord));

  // Recompute when the record's timestamps change (vote updates expiry)
  useEffect(() => {
    setPct(computePercentage(objectRecord));
  }, [objectRecord?.expire_at, objectRecord?.created_at, objectRecord?.is_permanent]);

  // Tick every 30s so the ring visibly drains over time
  useEffect(() => {
    const id = setInterval(() => setPct(computePercentage(objectRecord)), 30_000);
    return () => clearInterval(id);
  }, [objectRecord?.expire_at, objectRecord?.created_at]);

  const color = objectRecord?.is_permanent ? 'gold' : pickColor(pct);

  const tooltipLabel = (() => {
    if (objectRecord?.is_permanent) return 'Immortal — permanent';
    const expire = DateTime.fromISO(objectRecord?.expire_at);
    if (!expire.isValid) return '';
    const left = expire.diffNow(['days', 'hours', 'minutes']).toObject();
    const d = Math.floor(left.days ?? 0);
    const h = Math.floor(left.hours ?? 0);
    const m = Math.floor(left.minutes ?? 0);
    if (d < 0 || h < 0 || m < 0) return 'Expired';
    if (d > 0) return `${d}d ${h}h remaining (${pct}%)`;
    if (h > 0) return `${h}h ${m}m remaining (${pct}%)`;
    return `${m}m remaining (${pct}%)`;
  })();

  return (
    <Tooltip title={tooltipLabel} arrow placement="right">
    <Box sx={{ width: 52, height: 52, flexShrink: 0 }}>
      <CircularProgressbarWithChildren
        value={pct}
        styles={buildStyles({
          pathColor: color,
          trailColor: 'rgba(128,128,128,0.2)',
          pathTransitionDuration: 0.8,
        })}
      >
        {children}
      </CircularProgressbarWithChildren>
    </Box>
    </Tooltip>
  );
}

export default HealthBar;
